import { RootState, store } from "../store";
import { handleUnauthorizedError } from "./apiMiddleware";

const API_BASE_URL = `${import.meta.env.VITE_API_URL || "http://localhost:5000/api"}`;

const getAuthToken = (): string | null => {
  const state = store.getState() as RootState;
  return state.user.currentUser?.token || null;
};

const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

export interface Finance360OverviewParams {
  startDate?: string;
  endDate?: string;
  retailerId?: string;
  supplierId?: string;
  includeCashflow?: boolean;
}

export interface Finance360Range {
  startDate: string;
  endDate: string;
}

export interface Finance360OverviewResponse {
  range: Finance360Range;
  orders: {
    orderCount: number;
    totalSellIn: number;
    averageOrderValue: number;
    importCostTotal: number;
    retrocessionTotal: number;
    grossMargin: number;
    netMargin: number;
  };
  payments: {
    paidAmount: number;
    unpaidAmount: number;
    overdueAmount: number;
    avgPaymentDelayDays: number | null;
    avgDeliveryToPaymentDays: number | null;
  };
  cashflow: null | {
    revenue: number;
    expense: number;
    net: number;
    occurrenceCount: number;
  };
}

export interface Finance360SalesCycleResponse {
  totals: {
    retailerCount: number;
    launchedCount: number;
    lostCount: number;
    conversionRate: number;
  };
  stageCounts: Record<string, number>;
  closingCycle: {
    averageDays: number | null;
    medianDays: number | null;
  };
  stageDurations: Record<string, number | null>;
}

export interface Finance360RetailerSummary {
  retailerId: string;
  retailerName?: string;
  retailerStatus?: string;
  retailerSegment?: string;
  orderCount: number;
  totalSellIn: number;
  totalImportCost: number;
  totalRetrocession: number;
  netMargin: number;
  averageOrderValue: number;
  overdueAmount: number;
  avgPaymentDelayMs: number | null;
  avgPaymentDelayDays: number | null;
}

export interface Finance360RetailersResponse {
  range: Finance360Range;
  items: Finance360RetailerSummary[];
}

export interface Finance360SupplierSummary {
  supplierId: string;
  supplierName?: string;
  supplierGroup?: string;
  orderCount: number;
  totalSpend: number;
  totalImportCost: number;
  totalEstimatedCost: number;
  avgPaymentDelayDays: number | null;
  averageOrderValue: number;
}

export interface Finance360SuppliersResponse {
  range: Finance360Range;
  items: Finance360SupplierSummary[];
}

export interface Finance360InventoryTotals {
  totalUnits: number;
  sellableUnits: number;
  reservedUnits: number;
  defectiveUnits: number;
  totalValue: number;
  sellableValue: number;
  reservedValue: number;
  defectiveValue: number;
}

export interface Finance360InventoryStorageRow extends Finance360InventoryTotals {
  _id: string;
  storageName?: string;
}

export interface Finance360InventoryResponse {
  totals: Finance360InventoryTotals;
  byStorage: Finance360InventoryStorageRow[];
}

export interface Finance360WorkingCapitalResponse {
  range: Finance360Range;
  inventory: Finance360InventoryTotals;
  receivables: number;
  payables: number;
  workingCapital: number;
}

export interface Finance360ReceivablesPayablesResponse {
  range: Finance360Range;
  totals: { receivables: number; payables: number };
  overdue: { receivables: number; payables: number };
  net: number;
}

export interface Finance360AlertRetailer {
  name?: string;
  finance?: {
    creditLimit?: number;
    outstandingBalance?: number;
  };
  risk?: {
    score?: number;
  };
  status?: string;
}

export interface Finance360OverduePo {
  reference?: string;
  retailerId?: string;
  retailerName?: string;
  totalAmount?: number;
  paymentDueDate?: string | Date;
}

export interface Finance360AlertsResponse {
  range: Finance360Range;
  overduePos: Finance360OverduePo[];
  creditOverLimit: Finance360AlertRetailer[];
  highRiskRetailers: Finance360AlertRetailer[];
  cashflowNet: number;
  cashflowNegative: boolean;
}

export interface Finance360SalesTrendRow {
  _id: string;
  totalSellIn: number;
  importCost: number;
  retrocession: number;
  netMargin: number;
}

export interface Finance360SnapshotResponse {
  range: Finance360Range;
  overview: Finance360OverviewResponse;
  inventory: Finance360InventoryResponse;
  receivablesPayables: {
    totals: { receivables: number; payables: number };
    net: number;
  };
  workingCapital: Finance360WorkingCapitalResponse;
  salesCycle: Finance360SalesCycleResponse;
  salesTrend: Finance360SalesTrendRow[];
  dso: number | null;
  dpo: number | null;
  alerts: Finance360AlertsResponse;
  topRetailers: Finance360RetailerSummary[];
  topSuppliers: Finance360SupplierSummary[];
}

const buildQuery = (params: Record<string, string | number | boolean | undefined>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  return query.toString();
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (handleUnauthorizedError(response)) {
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }
  return (await response.json()) as T;
};

export const getFinance360Overview = async (
  params: Finance360OverviewParams = {}
): Promise<Finance360OverviewResponse> => {
  const query = buildQuery({
    startDate: params.startDate,
    endDate: params.endDate,
    retailerId: params.retailerId,
    supplierId: params.supplierId,
    includeCashflow: params.includeCashflow,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/overview${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360OverviewResponse>(response);
};

export const getFinance360SalesCycle = async (): Promise<Finance360SalesCycleResponse> => {
  const response = await fetch(`${API_BASE_URL}/finance/finance360/sales-cycle`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<Finance360SalesCycleResponse>(response);
};

export const getFinance360Retailers = async (params?: {
  startDate?: string;
  endDate?: string;
  retailerId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  limit?: number;
}): Promise<Finance360RetailersResponse> => {
  const query = buildQuery({
    startDate: params?.startDate,
    endDate: params?.endDate,
    retailerId: params?.retailerId,
    sortBy: params?.sortBy,
    sortDir: params?.sortDir,
    limit: params?.limit,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/retailers${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360RetailersResponse>(response);
};

export const getFinance360Suppliers = async (params?: {
  startDate?: string;
  endDate?: string;
  supplierId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  limit?: number;
}): Promise<Finance360SuppliersResponse> => {
  const query = buildQuery({
    startDate: params?.startDate,
    endDate: params?.endDate,
    supplierId: params?.supplierId,
    sortBy: params?.sortBy,
    sortDir: params?.sortDir,
    limit: params?.limit,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/suppliers${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360SuppliersResponse>(response);
};

export const getFinance360Inventory = async (params?: {
  limit?: number;
}): Promise<Finance360InventoryResponse> => {
  const query = buildQuery({ limit: params?.limit });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/inventory${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360InventoryResponse>(response);
};

export const getFinance360WorkingCapital = async (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<Finance360WorkingCapitalResponse> => {
  const query = buildQuery({
    startDate: params?.startDate,
    endDate: params?.endDate,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/working-capital${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360WorkingCapitalResponse>(response);
};

export const getFinance360ReceivablesPayables = async (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<Finance360ReceivablesPayablesResponse> => {
  const query = buildQuery({
    startDate: params?.startDate,
    endDate: params?.endDate,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/receivables-payables${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360ReceivablesPayablesResponse>(response);
};

export const getFinance360Alerts = async (params?: {
  startDate?: string;
  endDate?: string;
  riskScoreThreshold?: number;
}): Promise<Finance360AlertsResponse> => {
  const query = buildQuery({
    startDate: params?.startDate,
    endDate: params?.endDate,
    riskScoreThreshold: params?.riskScoreThreshold,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/alerts${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360AlertsResponse>(response);
};

export const getFinance360Snapshot = async (params?: {
  startDate?: string;
  endDate?: string;
  riskScoreThreshold?: number;
  topLimit?: number;
}): Promise<Finance360SnapshotResponse> => {
  const query = buildQuery({
    startDate: params?.startDate,
    endDate: params?.endDate,
    riskScoreThreshold: params?.riskScoreThreshold,
    topLimit: params?.topLimit,
  });
  const response = await fetch(
    `${API_BASE_URL}/finance/finance360/snapshot${query ? `?${query}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Finance360SnapshotResponse>(response);
};
